"use client";

import { ColumnDef } from "@tanstack/react-table";
import { Camera } from "@/schemas/camera";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreHorizontal, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SortableHeader } from "@/components/sortable_header";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { CameraActions } from "./camera-actions";
import { CameraStatus } from "./camera-status";
import { useBranch } from "@/context/branch-context";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";

const CameraBranchNameCell = ({ branchUuid }: { branchUuid: string | undefined }) => {
  const { branches } = useBranch();
  return (
    <span className="font-mono text-sm">
      {branches?.find((branch) => branch.bb_branch_id === branchUuid)?.name || "-"}
    </span>
  );
};

const CameraDetailCell = ({ row, getLink }: { row: { original: { id: string } }; getLink: (path: string) => string }) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      onClick={(event) => {
        event.preventDefault();
        navigate(getLink(`${row.original.id}`));
      }}
    >
      {t("common.details")}
    </Button>
  );
};

const TokenCell = ({ token }: { token: string }) => {
  const [showToken, setShowToken] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm">
        {showToken ? token : "••••••••"}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowToken(!showToken)}
        className="h-6 w-6 p-0"
      >
        {showToken ? (
          <EyeOff className="h-3 w-3" />
        ) : (
          <Eye className="h-3 w-3" />
        )}
      </Button>
    </div>
  );
};

export const columns: (
  getLink: (path: string) => string,
) => ColumnDef<Camera>[] = (getLink) => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={translate("cameras.table.selectAll")}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={translate("cameras.table.selectRow")}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "id",
    header: ({ column }) => (
      <SortableHeader column={column} label={translate("cameras.columns.deviceId")} />
    ),
  },
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.name")} />,
  },
  {
    id: "status",
    header: () => translate("common.status"),
    cell: ({ row }) => (
      <CameraStatus connectivity={row.original.connectivity} />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "branch_uuid",
    header: ({ column }) => (
      <SortableHeader column={column} label={translate("cameras.columns.branchUuid")} />
    ),
    cell: (info) => {
      const branchUuid = info.getValue() as string | undefined;
      return <span className="font-mono text-sm">{branchUuid || "-"}</span>;
    },
  },
  {
    accessorKey: "branch_uuid",
    id: "branch_name",
    header: ({ column }) => <SortableHeader column={column} label={translate("common.branch")} />,
    cell: (info) => {
      const branchUuid = info.getValue() as string | undefined;
      return <CameraBranchNameCell branchUuid={branchUuid} />;
    },
  },
  {
    accessorKey: "assigned",
    header: ({ column }) => <SortableHeader column={column} label={translate("cameras.assignment.assigned")} />,
    cell: (info) => {
      const assigned = info.getValue() as boolean;
      return (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            assigned
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
              : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          {assigned ? translate("common.yes") : translate("common.no")}
        </span>
      );
    },
  },
  {
    id: "assignment",
    header: () => translate("cameras.columns.assignment"),
    cell: ({ row }) => {
      const camera = row.original;
      return <CameraActions camera={camera} />;
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "token",
    header: () => translate("cameras.columns.token"),
    cell: (info) => {
      const token = info.getValue() as string;
      return <TokenCell token={token} />;
    },
    enableSorting: false,
  },
  {
    id: "detail",
    header: () => translate("common.details"),
    cell: ({ row }) => {
      return <CameraDetailCell row={row} getLink={getLink} />;
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const camera = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{translate("cameras.table.openMenu")}</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{translate("common.actions")}</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(camera.id)}
            >
              {translate("cameras.actions.copyDeviceId")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(camera.token)}
            >
              {translate("cameras.actions.copyToken")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                navigator.clipboard.writeText(JSON.stringify(camera))
              }
            >
              {translate("cameras.actions.copyData")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
